import BoldSmallText from '@/components/BoldSmallText';
import { optionLanguage } from '@/constants/options';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import languageRuleSlice, { initLanguageRule, selectedLanguageRuleSelector } from '@/redux/slice/languageRule.slice';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IResponseApi } from '@/types/apis/response';
import { Button, TextField } from '@shopify/polaris';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomTable from '../../../CustomTable';
import { EditETACheckoutStyled } from './styled';
import BoldText from '@/components/BoldText';
import { LanguageTranslateIcon } from '@shopify/polaris-icons';
import useTranslate from '@/hooks/useTranslate';
import useShippingMethodSeparator from '@/hooks/useShippingMethodSeparator';

interface IState {
  checkoutRules?: IResponseApi.ICheckoutRule[];
  checkoutAppearance?: IResponseApi.ICheckoutAppearance;
  isLoading: boolean;
}

const EditETACheckout = () => {
  const shippingMethodSeparator = useShippingMethodSeparator();
  const dispatch = useDispatch();
  const { translateText, isShowTranslate } = useTranslate();
  const isSkip = useSelector(isSkipApiSelector);
  const currentLanguageRule = useSelector(selectedLanguageRuleSelector);
  const dataSettings = useSelector(dataSettingsSelector);

  const [state, setState] = useState<IState>({
    isLoading: false,
  });

  const { data: allCheckoutRules } = apiCaller.useGetCheckoutRulesQuery({ page: 1, perPage: -1 }, { skip: isSkip });
  const { data: checkoutAppearance } = apiCaller.useGetCheckoutAppearanceQuery(undefined, {
    skip: isSkip,
  });

  useEffect(() => {
    setState((prev) => ({ ...prev, checkoutRules: allCheckoutRules?.data, checkoutAppearance: checkoutAppearance?.data }));
  }, [allCheckoutRules?.data, checkoutAppearance?.data]);

  const dataRow: {
    id: string;
    group?: string;
    label?: string;
    value?: string;
    field?: 'checkoutRuleMessageText' | 'productTimeLine' | 'thankYouAndOrderStatus';
  }[] = useMemo(() => {
    return [
      {
        id: '-1',
        group: 'Rule’s message text',
      },
      ...(state.checkoutRules?.map((rule) => ({
        id: rule.id.toString(),
        label: rule.shippingMethods.split(shippingMethodSeparator).join(', '),
        value: rule.messageText,
        field: 'checkoutRuleMessageText' as const,
      })) || []),
      {
        id: '-2',
        group: 'Product line items',
      },
      {
        id: '-3',
        label: 'Label text',
        value: state.checkoutAppearance?.labelText || ' ',
        field: 'productTimeLine' as const,
      },
      {
        id: '-4',
        group: 'Thank you and order status',
      },
      {
        id: '-5',
        label: 'Title',
        value: state.checkoutAppearance?.title || ' ',
        field: 'thankYouAndOrderStatus' as const,
      },
    ];
  }, [shippingMethodSeparator, state.checkoutAppearance?.labelText, state.checkoutAppearance?.title, state.checkoutRules]);

  const handleChangeCheckoutContent = useCallback(
    (id: string, field: string, value: string) => {
      if (!currentLanguageRule.checkoutContent) return;
      if (field === 'checkoutRuleMessageText') {
        let currentCheckoutRules = currentLanguageRule.checkoutContent.checkoutRules;
        const currentCheckoutRule = currentCheckoutRules.find((rule) => rule.ruleId === +id);
        currentCheckoutRules = [
          ...currentCheckoutRules.filter((rule) => rule.ruleId !== +id),
          { id: currentCheckoutRule?.id || 0, ruleId: +id, messageText: value },
        ];
        return dispatch(
          languageRuleSlice.actions.handleChangeSelectedRule({
            key: 'checkoutContent',
            value: { ...currentLanguageRule.checkoutContent, checkoutRules: currentCheckoutRules },
          }),
        );
      } else {
        const temp = { ...currentLanguageRule.checkoutContent, [field]: value };
        dispatch(languageRuleSlice.actions.handleChangeSelectedRule({ key: 'checkoutContent', value: temp }));
      }
    },
    [dispatch, currentLanguageRule.checkoutContent],
  );

  const rowMarkup = useMemo(() => {
    return dataRow.map(({ id, group, label, value, field }) => {
      if (group) {
        return (
          <tr id={id} key={id}>
            <td className="bg-gray" colSpan={3}>
              <BoldSmallText>{group}</BoldSmallText>
            </td>
          </tr>
        );
      }
      return (
        <tr id={id} key={id}>
          {label ? <td>{label}</td> : null}
          {value ? <td>{value}</td> : null}
          <td>
            <TextField
              label=""
              autoComplete="off"
              onChange={(inputValue) => field && handleChangeCheckoutContent(id, field, inputValue)}
              value={
                field === 'checkoutRuleMessageText'
                  ? currentLanguageRule.checkoutContent?.checkoutRules.find((rule) => rule.ruleId === +id)?.messageText
                  : field
                  ? currentLanguageRule.checkoutContent?.[field]
                  : undefined
              }
            />
          </td>
        </tr>
      );
    });
  }, [currentLanguageRule.checkoutContent, dataRow, handleChangeCheckoutContent]);

  const headings = [
    {
      title: '',
      width: '15%',
    },
    {
      title: `Default language (${optionLanguage.find((option) => option.value === dataSettings?.date_locale)?.label})`,
      width: '30%',
    },
    {
      title: currentLanguageRule?.nameLanguage,
      width: '55%',
    },
  ];

  const handleTranslate = async () => {
    const translateData = { ...currentLanguageRule.checkoutContent };
    setState((prev) => ({ ...prev, isLoading: true }));
    const fieldsToTranslate: { key: 'productTimeLine' | 'thankYouAndOrderStatus'; source?: string }[] = [
      { key: 'productTimeLine', source: state.checkoutAppearance?.labelText },
      { key: 'thankYouAndOrderStatus', source: state.checkoutAppearance?.title },
    ];

    await Promise.all(
      fieldsToTranslate.map(async ({ key, source }) => {
        if (!translateData[key]) {
          translateData[key] = await translateText(source || '');
        }
      }),
    );

    translateData.checkoutRules = state.checkoutRules
      ? await Promise.all(
          state.checkoutRules.map(async (rule) => {
            const existedRule = translateData.checkoutRules?.find((item) => item.ruleId === rule.id);
            const res = {
              id: existedRule?.id || 0,
              ruleId: rule.id,
              messageText: existedRule?.messageText || (await translateText(rule.messageText)),
            };
            return res;
          }),
        )
      : [];

    dispatch(
      languageRuleSlice.actions.handleChangeSelectedRule({
        key: 'checkoutContent',
        value: { ...(currentLanguageRule.checkoutContent || initLanguageRule.checkoutContent), ...translateData },
      }),
    );

    setState((prev) => ({ ...prev, isLoading: false }));
  };

  return (
    <EditETACheckoutStyled>
      {isShowTranslate ? (
        <div className="d-flex justify-between align-center">
          <BoldText>Appearance content</BoldText>
          <Button icon={LanguageTranslateIcon} onClick={handleTranslate} loading={state.isLoading}>
            Auto-translate
          </Button>
        </div>
      ) : null}
      <div className="table-container">
        <CustomTable headings={headings} rowMarkup={rowMarkup} />
      </div>
    </EditETACheckoutStyled>
  );
};

export default memo(EditETACheckout);
